import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
  const [ready, setReady] = useState(false);

  // The organization context always comes from /auth/me, never from the login
  // response or local storage: the server is the only authority on which
  // organization a session may act in.
  const loadMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  useEffect(() => {
    // HttpOnly cookies cannot be inspected from JavaScript. Ask the server on
    // every application start; a 401 is the signed-out state.
    loadMe()
      .catch(() => {
        toast.dismiss();
        setUser(false);
      })
      .finally(() => setReady(true));
  }, [loadMe]);

  const login = () => {
    // Sonner keeps a process-wide toast store. Clear messages from the prior
    // unauthenticated/persona context before establishing the new identity.
    toast.dismiss();
    return loadMe();
  };

  const logout = () => {
    // Shared workstations must never show one persona's feedback to the next.
    toast.dismiss();
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        ready,
        login,
        logout,
        refresh: loadMe,
        organization: user?.organization || null,
        isPlatformAdmin: !!user?.is_platform_admin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
